import {
  CheckSquare,
  Users,
  BarChart3,
  Target,
  Gauge,
  Activity,
  Zap,
  TrendingUp,
} from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'Busy work obscures the priority',
    description: 'A long task list can hide which projects support your most important objectives.',
    icon: CheckSquare,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Responsibility is assumed',
    description:
      'Work is harder to follow up when ownership and due dates are scattered across conversations.',
    icon: Users,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Status lacks context',
    description:
      'An isolated update tells you what happened. It is harder to see how that work fits into the broader strategy.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Connected work hierarchy',
    description: 'Trace an objective through its strategies, projects, tasks, and subtasks.',
    icon: Target,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Clear responsibility',
    description:
      'Assign project and task owners and connect work to positions in your organisation.',
    icon: Users,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Dates and priorities',
    description: 'Give work a due date and priority so the next action is easier to identify.',
    icon: Gauge,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Progress you can follow',
    description: 'Update work status and see project progress alongside the strategy it supports.',
    icon: Activity,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI work breakdowns',
    description: 'Use your strategy and project context to draft projects, tasks, and subtasks.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Views for different questions',
    description: 'Explore the hierarchy or work through a board, list, map, or timeline.',
    icon: TrendingUp,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function StrategyExecutionSoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Make every project serve a strategic goal."
      subheadline="Connect objectives to strategies, projects, and tasks. Give the work clear owners and deadlines, then see progress in the context of your plan."
      bullets={[
        'A visible path from objectives to tasks',
        'Assigned owners and deadlines',
        'Project and task progress',
        'Hierarchy, board, list, map, and timeline views',
      ]}
      badgeText="Strategy Execution"
      problemTitle="The gap between a goal and a task costs clarity."
      problemSubtitle="Execution becomes difficult to steer when leaders can see activity but cannot easily see the strategic purpose behind it."
      problemCards={problemCards}
      solutionTitle="Give strategic work a clear home."
      solutionSubtitle="The Execution Hub keeps the objective, strategy, project, and task relationship visible as you organise and update the work."
      solutionSteps={[
        {
          title: 'Connect the work to a goal',
          description:
            'Link each project to a strategy and each strategy to an objective, so the reason for the work stays visible.',
        },
        {
          title: 'Make the next actions specific',
          description:
            'Break projects into tasks and subtasks. Assign responsibility, set priorities, and add deadlines.',
        },
        {
          title: 'Follow delivery in context',
          description:
            'Use the hierarchy for the bigger picture and switch to board, list, map, or timeline views to manage the details.',
        },
      ]}
      featuresTitle="Keep purpose, ownership, and progress together"
      features={features}
      finalCtaTitle="Give your strategy a clear next action."
      finalCtaSubtitle="Create your account and connect your strategic priorities to the work that moves them forward."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="strategy_execution_landing"
      breadcrumbLabel="Strategy Execution"
    />
  );
}
